import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  DocumentTextIcon,
  VideoCameraIcon,
  GlobeAltIcon,
  FunnelIcon,
  LanguageIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  WrenchScrewdriverIcon,
  SparklesIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  HandThumbUpIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  ArrowTrendingUpIcon,
  ScaleIcon,
  ChatBubbleLeftRightIcon,
  UserIcon,
  BookOpenIcon,
  MapIcon,
  BuildingOffice2Icon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { Grid, Container, Tabs, Tab, Paper, FormControl, Select, MenuItem, InputLabel, Dialog, DialogTitle, DialogContent, IconButton } from '@material-ui/core';
import SingleColumnTemplate from '../../../../ui/layouts/SingleColumnTemplate.jsx';
import MarkdownViewer from './MarkdownViewer';
import Button from '../../../../ui/canon/Button';
import './ArticleProse.css';

// Average adult reading speed for considered, non-skimming prose.
const WORDS_PER_MINUTE = 220;

const readingMinutes = (body) => {
  const words = (body || '').trim().split(/\s+/).filter(Boolean).length;
  return words ? Math.max(1, Math.round(words / WORDS_PER_MINUTE)) : 0;
};

// Modal component for displaying content details
const ContentModal = ({ open, onClose, content, siblings = [], onNavigate }) => {
  const intl = useIntl();

  // Position within the currently displayed (already sorted) category list, so
  // "next" follows the same Part 1..5 order the cards are shown in.
  const currentIndex = content
    ? siblings.findIndex(item => item.id === content.id)
    : -1;
  const previousContent = currentIndex > 0 ? siblings[currentIndex - 1] : null;
  const nextContent = currentIndex >= 0 && currentIndex < siblings.length - 1
    ? siblings[currentIndex + 1]
    : null;
  const showSeriesNav = Boolean(onNavigate && (previousContent || nextContent));
  const minutes = readingMinutes(content?.mdBody);
  // Handle property names that might be in snake_case from Rails
  const contentType = content?.contentType || content?.content_type || '';
  const rawUrl = content?.url || content?.asset_url || '';
  const rawEmbedCode = content?.embedCode || content?.embed_code || '';
  
  // Detect if the URL actually contains HTML embed code (for legacy data)
  const isHtmlEmbed = (str) => str && (str.trim().startsWith('<') || str.includes('<iframe'));
  
  // If embedCode is empty but url contains HTML, use url as embedCode
  const embedCode = rawEmbedCode || (isHtmlEmbed(rawUrl) ? rawUrl : '');
  // Only use url as actual URL if it's not HTML
  const url = isHtmlEmbed(rawUrl) ? '' : rawUrl;
  
  // Handle PDF download
  const handleDownload = () => {
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        // Create a temporary URL for the blob
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Create a link element and trigger download
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = content.title ? `${content.title.replace(/\s+/g, '_')}.pdf` : 'document.pdf';
        document.body.appendChild(a);
        a.click();
        
        // Clean up
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(a);
      })
      .catch(error => {
        console.error('Error downloading PDF:', error);
        // Fallback to opening in new tab if download fails
        window.open(url, '_blank');
      });
  };
  
  // Render content based on type
  const renderContent = () => {
    if (!content) return null;
    
    // Treat markdown if explicitly set or if mdBody is present
    if (content?.mdBody || contentType === 'markdown') {
      return (
        <div className="mt-4">
          <MarkdownViewer body={content.mdBody} />
          <div className="fb-article-actions">
            <Button
              as="a"
              href={`/educational_contents/${content.id}.pdf`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
              {intl.formatMessage({ id: 'succession.education.download_pdf' })}
            </Button>
          </div>
        </div>
      );
    }

    switch(contentType) {
      case 'iframe':
        // If embed_code contains full HTML (like a YouTube embed), render it directly
        if (embedCode) {
          return (
            <div 
              className="w-full mt-4 rounded overflow-hidden flex justify-center"
              dangerouslySetInnerHTML={{ __html: embedCode }}
            />
          );
        }
        // Otherwise use the URL as iframe src
        return (
          <div className="w-full h-96 mt-4 rounded overflow-hidden">
            <iframe 
              src={url} 
              title={content.title}
              className="w-full h-full border-0" 
              allowFullScreen
            />
          </div>
        );
      case 'document':
        return (
          <div className="mt-6 flex justify-center">
            <Button type="button" onClick={handleDownload}>
              <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
              {intl.formatMessage({ id: 'succession.education.download_pdf' })}
            </Button>
          </div>
        );
      case 'video':
        // Check if this is a YouTube URL and convert to embed format
        const getYouTubeEmbedUrl = (videoUrl) => {
          if (!videoUrl) return null;
          // Match various YouTube URL formats
          const patterns = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/,
            /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]+)/
          ];
          for (const pattern of patterns) {
            const match = videoUrl.match(pattern);
            if (match && match[1]) {
              return `https://www.youtube.com/embed/${match[1]}`;
            }
          }
          return null;
        };
        
        // Check if this is a Vimeo URL and convert to embed format
        const getVimeoEmbedUrl = (videoUrl) => {
          if (!videoUrl) return null;
          const match = videoUrl.match(/vimeo\.com\/(\d+)/);
          if (match && match[1]) {
            return `https://player.vimeo.com/video/${match[1]}`;
          }
          return null;
        };
        
        const youtubeEmbedUrl = getYouTubeEmbedUrl(url);
        const vimeoEmbedUrl = getVimeoEmbedUrl(url);
        
        // If we have embed code, render it directly
        if (embedCode) {
          return (
            <div 
              className="w-full mt-4 rounded overflow-hidden flex justify-center"
              dangerouslySetInnerHTML={{ __html: embedCode }}
            />
          );
        }
        
        // If it's a YouTube video, use iframe embed
        if (youtubeEmbedUrl) {
          return (
            <div className="w-full mt-4 rounded overflow-hidden">
              <iframe
                src={youtubeEmbedUrl}
                title={content.title}
                className="w-full aspect-video border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }
        
        // If it's a Vimeo video, use iframe embed
        if (vimeoEmbedUrl) {
          return (
            <div className="w-full mt-4 rounded overflow-hidden">
              <iframe
                src={vimeoEmbedUrl}
                title={content.title}
                className="w-full aspect-video border-0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }
        
        // Default to native video player for direct video files
        return (
          <div className="w-full mt-4 rounded overflow-hidden">
            <video
              src={url}
              controls
              className="w-full max-h-[600px]"
              poster={content.thumbnailUrl || content.thumbnail_url}
            >
              {intl.formatMessage({ id: 'succession.education.video_not_supported' })}
            </video>
          </div>
        );
      default:
        return (
          <div className="mt-4 text-center text-succession-text-secondary">
            <p>{intl.formatMessage({ id: 'succession.education.content_cannot_display' })}</p>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-succession-primary hover:underline mt-2 inline-block"
              >
                {intl.formatMessage({ id: 'succession.education.open_in_new_tab' })}
              </a>
            )}
          </div>
        );
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      aria-labelledby="content-modal-title"
    >
      <DialogTitle id="content-modal-title">
        <div className="flex justify-between items-start gap-4 pr-2">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wider text-succession-text-secondary mb-1">
              {content?.category}
              {minutes > 0 && (
                <span className="ml-2 font-normal normal-case tracking-normal">
                  {intl.formatMessage({ id: 'succession.education.minute_read' }, { minutes })}
                </span>
              )}
            </div>
            <span className="block text-xl font-semibold leading-tight text-succession-text-primary">
              {content?.title || intl.formatMessage({ id: 'succession.education.content_details' })}
            </span>
          </div>
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
          >
            <XMarkIcon className="h-6 w-6" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        {/* The summary is a deck, not the first paragraph — sized between the
            title and the body so it reads as an abstract. */}
        <div className="fb-article-deck">
          {content?.description || intl.formatMessage({ id: 'succession.education.no_description' })}
        </div>
        {renderContent()}
        {showSeriesNav && (
          <div className="fb-article-serienav">
            {[
              { item: previousContent, label: intl.formatMessage({ id: 'succession.education.previous_in_series' }) },
              { item: nextContent, label: intl.formatMessage({ id: 'succession.education.next_in_series' }) },
            ].map(({ item, label }) => (
              item ? (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onNavigate(item)}
                  className="fb-article-serienav__item"
                >
                  <span className="fb-article-serienav__label">{label}</span>
                  <span className="fb-article-serienav__title">{item.title}</span>
                </button>
              ) : <div key={label} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Map English category names to i18n keys
const CATEGORY_KEY_MAP = {
  'All': 'all',
  'Uncategorized': 'uncategorized',
  'Identify': 'identify',
  'Protect': 'protect',
  'Manage': 'manage',
  'Build': 'build',
  'Harvest': 'harvest',
  'Buy-Sell Education': 'buy_sell_education',
  'Catastrophic Planning': 'catastrophic_planning',
  'Succession Planning': 'succession_planning',
  'Mergers & Acquisitions': 'mergers_acquisitions',
  'Next-Gen Development': 'nextgen_development',
  'Sample Agreements': 'sample_agreements',
  'Valuation': 'valuation',
  'Regulatory': 'regulatory',
  'Client Communications': 'client_communications',
  'Contact': 'contact',
};

// Fallback visual when no thumbnail is present
const EducationCardFallback = ({ category, className = '' }) => {
  const intl = useIntl();
  const map = {
    identify: { icon: MagnifyingGlassIcon, labelKey: 'succession.education.category.identify' },
    build: { icon: WrenchScrewdriverIcon, labelKey: 'succession.education.category.build' },
    harvest: { icon: SparklesIcon, labelKey: 'succession.education.category.harvest' },
    manage: { icon: Cog6ToothIcon, labelKey: 'succession.education.category.manage' },
    protect: { icon: ShieldCheckIcon, labelKey: 'succession.education.category.protect' },
    'buy-sell education': { icon: HandThumbUpIcon, labelKey: 'succession.education.category.buy_sell_education' },
    'catastrophic planning': { icon: ExclamationTriangleIcon, labelKey: 'succession.education.category.catastrophic_planning' },
    'succession planning': { icon: MapIcon, labelKey: 'succession.education.category.succession_planning' },
    'mergers & acquisitions': { icon: BuildingOffice2Icon, labelKey: 'succession.education.category.mergers_acquisitions' },
    'next-gen development': { icon: UserGroupIcon, labelKey: 'succession.education.category.nextgen_development' },
    'sample agreements': { icon: PencilSquareIcon, labelKey: 'succession.education.category.sample_agreements' },
    valuation: { icon: ArrowTrendingUpIcon, labelKey: 'succession.education.category.valuation' },
    regulatory: { icon: ScaleIcon, labelKey: 'succession.education.category.regulatory' },
    'client communications': { icon: ChatBubbleLeftRightIcon, labelKey: 'succession.education.category.client_communications' },
    contact: { icon: UserIcon, labelKey: 'succession.education.category.contact' },
  };

  const key = (category || '').toString().trim().toLowerCase();
  const cfg = map[key];

  // Map categories to soft gradients using marketplace theme rgb variables
  const rgbMap = {
    identify: 'var(--company-primary-rgb)',
    build: 'var(--company-secondary-rgb)',
    harvest: 'var(--company-accent-rgb)',
    manage: 'var(--company-neutral-rgb)',
    protect: 'var(--company-accent-rgb)',
    'buy-sell education': 'var(--company-secondary-rgb)',
    'catastrophic planning': 'var(--company-accent-rgb)',
    'succession planning': 'var(--company-primary-rgb)',
    'mergers & acquisitions': 'var(--company-secondary-rgb)',
    'next-gen development': 'var(--company-accent-rgb)',
    'sample agreements': 'var(--company-neutral-rgb)',
    valuation: 'var(--company-primary-rgb)',
    regulatory: 'var(--company-secondary-rgb)',
    'client communications': 'var(--company-accent-rgb)',
    contact: 'var(--company-neutral-rgb)'
  };
  const rgb = rgbMap[key] || 'var(--company-neutral-rgb)';
  const gradientStyle = {
    background: `linear-gradient(135deg, rgba(${rgb}, 0.14) 0%, rgba(${rgb}, 0.06) 100%)`
  };

  if (!cfg) {
    return (
      <div className={`relative w-full h-full flex items-center justify-center ${className}`} style={gradientStyle}>
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="p-3 rounded-lg bg-succession-bg-secondary border border-succession-border">
            <BookOpenIcon className="w-10 h-10 text-succession-text-secondary" strokeWidth={1.5} />
          </div>
          <div className="text-xs font-medium text-succession-text-secondary">{intl.formatMessage({ id: 'succession.education.educational_material' })}</div>
        </div>
        <DocumentTextIcon className="absolute top-3 right-3 w-5 h-5 text-succession-text-secondary/20" />
      </div>
    );
  }

  const Icon = cfg.icon;
  return (
    <div className={`relative w-full h-full flex items-center justify-center border-l-4 ${className}`} style={{ ...gradientStyle, borderLeftColor: `rgba(${rgb}, 0.35)` }}>
      <div className="relative flex flex-col items-center justify-center gap-4 px-4">
        <div className="p-4 rounded-xl bg-succession-bg-primary border-2 border-succession-border shadow-sm">
          <Icon className="w-10 h-10 text-succession-text-primary" strokeWidth={1.5} />
        </div>
        <div className="flex flex-col items-center gap-1">
          <div className="text-sm font-semibold text-succession-text-primary">{intl.formatMessage({ id: cfg.labelKey })}</div>
        </div>
      </div>
      <DocumentTextIcon className="absolute top-3 right-3 w-5 h-5 text-succession-text-secondary/20" />
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-succession-primary/10 to-transparent" />
    </div>
  );
};

// Component for an individual content card
const ContentCard = ({ content, onCardClick }) => {
  const intl = useIntl();
  // Handle property names that might be in snake_case from Rails
  const contentType = content.contentType || content.content_type;
  const thumbnailUrl = content.thumbnailUrl || content.thumbnail_url;
  
  // Determine icon based on content type with themed colors
  const getIcon = () => {
    switch(contentType) {
      case 'document':
        return <DocumentTextIcon className="h-6 w-6 text-succession-primary transition-colors duration-300" />;
      case 'video':
        return <VideoCameraIcon className="h-6 w-6 text-succession-error transition-colors duration-300" />;
      case 'iframe':
        return <GlobeAltIcon className="h-6 w-6 text-succession-success transition-colors duration-300" />;
      default:
        return <DocumentTextIcon className="h-6 w-6 text-succession-text-secondary transition-colors duration-300" />;
    }
  };
  
  // Handle click on card
  const handleClick = () => {
    onCardClick(content);
  };

  const imageUrl = thumbnailUrl || null;

  return (
    <div className="rounded-lg shadow-succession-md overflow-hidden bg-succession-bg-primary cursor-pointer transform transition-all duration-300 hover:shadow-succession-lg hover:-translate-y-1 border border-succession-border" 
         onClick={handleClick}>
      <div className="relative h-48 bg-succession-bg-tertiary transition-colors duration-300">
        {imageUrl ? (
          <img src={imageUrl} alt={content.title} className="h-full w-full object-cover" />
        ) : (
          <EducationCardFallback category={content.category} />
        )}
        <div className="absolute top-2 right-2 bg-succession-bg-primary rounded-full p-1 border border-succession-border transition-colors duration-300">
          {getIcon()}
        </div>
        {content.locale && (
          <div className="absolute top-2 left-2 bg-succession-bg-primary rounded-full p-1 text-xs font-bold text-succession-text-primary border border-succession-border transition-colors duration-300">
            {content.locale.toUpperCase()}
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-lg font-semibold text-succession-text-primary truncate transition-colors duration-300">{content.title}</h3>
        <p className="text-sm text-succession-text-secondary mt-1 line-clamp-2 h-10 overflow-hidden transition-colors duration-300">
          {content.description || intl.formatMessage({ id: 'succession.education.no_description' })}
        </p>
      </div>
      <div className="px-4 py-2 border-t border-succession-border flex items-center text-xs text-succession-text-muted transition-colors duration-300">
        <span>{intl.formatMessage({ id: `succession.education.content_type.${contentType}`, defaultMessage: contentType })}</span>
        {content.category && (
          <>
            <span className="mx-1">•</span>
            <span>{(() => {
              const key = CATEGORY_KEY_MAP[content.category];
              return key ? intl.formatMessage({ id: `succession.education.category.${key}`, defaultMessage: content.category }) : content.category;
            })()}</span>
          </>
        )}
      </div>
    </div>
  );
};

// Main Education component
const Education = ({ educationalContents, currentUserLocale }) => {
  const intl = useIntl();
  // Set the initial selectedLocale based on user's locale, defaulting to 'en' if not provided
  const getUserLocale = () => {
    if (!currentUserLocale) return 'en';
    // If locale is 'fr', set it as 'fr'
    return currentUserLocale.toLowerCase();
  };

  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocale, setSelectedLocale] = useState(getUserLocale());
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);

  // Update locale if the user's locale changes
  useEffect(() => {
    if (currentUserLocale) {
      setSelectedLocale(currentUserLocale.toLowerCase());
    }
  }, [currentUserLocale]);

  // Log content for debugging
  console.log('Educational contents:', educationalContents);

  // Helper to detect if a string contains HTML embed code
  const isHtmlEmbed = (str) => str && (str.trim().startsWith('<') || str.includes('<iframe'));

  // Transform content to ensure all properties are present
  const normalizedContents = educationalContents.map(content => {
    const rawUrl = content.url || content.asset_url || '';
    const rawEmbedCode = content.embedCode || content.embed_code || '';
    
    // Handle case where embed code might be stored in asset_url for legacy data
    const embedCode = rawEmbedCode || (isHtmlEmbed(rawUrl) ? rawUrl : '');
    const url = isHtmlEmbed(rawUrl) ? '' : rawUrl;
    
    return {
      id: content.id,
      title: content.title,
      description: content.description,
      url: url, // Only use as URL if it's not HTML embed code
      contentType: content.contentType || content.content_type,
      thumbnailUrl: content.thumbnailUrl || content.thumbnail_url,
      embedCode: embedCode, // Include embed code for iframes
      category: content.category || 'Uncategorized', // Provide default category
      locale: content.locale || 'en',
      visible: content.visible !== undefined ? content.visible : true,
      createdAt: content.createdAt || content.created_at,
      updatedAt: content.updatedAt || content.updated_at,
      mdBody: content.md_body || content.mdBody,
      mdPath: content.md_path || content.mdPath,
    };
  });

  // Get all categories from content
  const categories = ['All', ...new Set(normalizedContents.map(content => content.category).filter(Boolean))];

  // If a category is provided in the URL (e.g., ?category=Value%20Acceleration), preselect it
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const params = new URLSearchParams(window.location.search);
      const cat = params.get('category') || params.get('cat');
      if (!cat) return;
      const idx = categories.findIndex((c) => c && c.toString().toLowerCase() === cat.toString().toLowerCase());
      if (idx >= 0 && idx !== activeTab) setActiveTab(idx);
    } catch (_) {}
  }, [categories]);
  
  // Get all available locales from content
  const availableLocales = [...new Set(normalizedContents.map(content => content.locale).filter(Boolean))];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleLocaleChange = (event) => {
    setSelectedLocale(event.target.value);
  };

  // Handle card click to open modal
  const handleCardClick = (content) => {
    setSelectedContent(content);
    setModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
  };

  // Helper functions for SingleColumnTemplate
  const getPageTitle = () => {
    return intl.formatMessage({ id: 'succession.education.title' });
  };

  const getPageSubtitle = () => {
    return intl.formatMessage({ id: 'succession.education.subtitle' });
  };

  const getActionButtons = () => {
    // No action buttons for education page
    return [];
  };

  // Filter content by category, search term, and locale
  const filteredContent = normalizedContents.filter(content => {
    const contentLocale = content.locale ? content.locale.toLowerCase() : 'en';
    const matchesCategory = activeTab === 0 || content.category === categories[activeTab];
    const matchesSearch = content.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (content.description && content.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesLocale = selectedLocale === 'all' || contentLocale === selectedLocale;
    return matchesCategory && matchesSearch && matchesLocale;
  });

  // Sort part-based series by part number (e.g., "Part 1", "Part 2", ...)
  const PART_SORTED_CATEGORIES = [
    'value acceleration',
    'catastrophic planning',
    'succession planning',
    'mergers & acquisitions',
    'next-gen development',
    'valuation',
  ];

  const extractPartNumber = (title) => {
    if (!title) return Number.POSITIVE_INFINITY;
    const match = title.match(/part\s*(\d+)/i);
    return match ? parseInt(match[1], 10) : Number.POSITIVE_INFINITY;
  };

  const selectedCategory = categories[activeTab];
  const displayedContent = React.useMemo(() => {
    if (PART_SORTED_CATEGORIES.includes((selectedCategory || '').toString().toLowerCase())) {
      return [...filteredContent].sort((a, b) => {
        const partA = extractPartNumber(a.title);
        const partB = extractPartNumber(b.title);
        // Un-numbered entries share POSITIVE_INFINITY; subtracting would yield NaN,
        // so compare for equality first and leave their relative order alone.
        return partA === partB ? 0 : partA - partB;
      });
    }
    return filteredContent;
  }, [filteredContent, selectedCategory]);

  return (
    <>
      <SingleColumnTemplate
        title={getPageTitle()}
        subtitle={getPageSubtitle()}
        actions={getActionButtons()}
        columnClassName="max-w-2xl"
        variant="wide"
      >
        <div className="space-y-6 w-full overflow-hidden max-w-full">
          {/* Language and Filters */}
          <div className="bg-succession-bg-primary rounded-lg p-4 border border-succession-border shadow-succession-md transition-colors duration-300">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-6 w-full">
              <div className="flex items-center min-w-0">
                <LanguageIcon className="h-5 w-5 text-succession-text-secondary mr-2 flex-shrink-0 transition-colors duration-300" />
                <FormControl variant="outlined" size="small" className="min-w-0 w-full max-w-xs">
                  <InputLabel
                    id="locale-select-label"
                    style={{ color: 'var(--succession-text-secondary)' }}
                  >
                    {intl.formatMessage({ id: 'succession.education.language_label' })}
                  </InputLabel>
                  <Select
                    labelId="locale-select-label"
                    id="locale-select"
                    value={selectedLocale}
                    onChange={handleLocaleChange}
                    label={intl.formatMessage({ id: 'succession.education.language_label' })}
                    style={{
                      backgroundColor: 'var(--succession-bg-primary)',
                      color: 'var(--succession-text-primary)'
                    }}
                  >
                    <MenuItem value="all">{intl.formatMessage({ id: 'succession.education.all_languages' })}</MenuItem>
                    <MenuItem value="en">{intl.formatMessage({ id: 'succession.education.english' })}</MenuItem>
                    <MenuItem value="fr">{intl.formatMessage({ id: 'succession.education.french' })}</MenuItem>
                    {availableLocales
                      .filter(locale => locale !== 'en' && locale !== 'fr')
                      .map(locale => (
                        <MenuItem key={locale} value={locale.toLowerCase()}>{locale.toUpperCase()}</MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </div>

              {/* Search Input */}
              <div className="flex items-center min-w-0 flex-1 max-w-md lg:max-w-sm xl:max-w-md">
                <FunnelIcon className="h-5 w-5 text-succession-text-secondary mr-2 flex-shrink-0 transition-colors duration-300" />
                <input
                  type="text"
                  placeholder={intl.formatMessage({ id: 'succession.education.search_placeholder' })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full max-w-full px-3 py-2 border border-succession-border rounded-md bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted focus:outline-none focus:ring-2 focus:ring-succession-primary focus:border-transparent transition-colors duration-300"
                />
              </div>
            </div>
          </div>

          {/* Category tabs */}
          <div className="bg-succession-bg-primary rounded-lg mb-6 border border-succession-border shadow-succession-md transition-colors duration-300 w-full overflow-hidden">
            <div className="w-full overflow-x-auto">
              <div className="flex space-x-1 p-2 min-w-max">
                {categories.map((category, index) => {
                  const catKey = CATEGORY_KEY_MAP[category];
                  const label = catKey ? intl.formatMessage({ id: `succession.education.category.${catKey}`, defaultMessage: category }) : category;
                  return (
                    <button
                      key={index}
                      onClick={() => handleTabChange({}, index)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-300 whitespace-nowrap flex-shrink-0 ${
                        activeTab === index
                          ? 'bg-succession-primary text-white'
                          : 'text-succession-text-secondary hover:text-succession-primary hover:bg-succession-bg-secondary'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Content grid */}
          <div className="w-full overflow-hidden">
            {displayedContent.length === 0 ? (
              <div className="flex justify-center items-center h-64 bg-succession-bg-primary rounded-lg border border-succession-border transition-colors duration-300">
                <div className="text-succession-text-secondary transition-colors duration-300">
                  {intl.formatMessage({ id: 'succession.education.no_content_filtered' })}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-4 w-full overflow-hidden">
                {displayedContent.map((content) => (
                  <div key={content.id} className="w-full min-w-0 overflow-hidden">
                    <ContentCard content={content} onCardClick={handleCardClick} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </SingleColumnTemplate>

      {/* Content Modal */}
      <ContentModal
        siblings={displayedContent}
        onNavigate={setSelectedContent}
        open={modalOpen}
        onClose={handleModalClose}
        content={selectedContent}
      />
    </>
  );
};

export default Education; 